import React, {useState, useRef, useMemo} from 'react';
import {useParams, useLocation} from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Slider,
  Switch,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  Button,
  Headline,
  Body,
  Label,
  Icon,
  designSystemToken,
  Tabs,
} from '@lightricks/react-design-system';
import {TabData} from '@lightricks/react-design-system/dist/components/tabs/TabsProps';
import Sentry from '@/services/sentry/Sentry';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {BrandedContentAdsPartnership} from '@/types/brandedContentAdsPartnership';
import {SwapMatch} from '@/types/swapMatch';
import FlashMessage from '@/components/flash-message';
import InstagramPostPreview from '@/components/partnership-ads-modal/instagram-post-preview';
import {PartnershipAdFormData} from '@/components/partnership-ads-modal/partnership-ad-form/PartnershipAdForm';
import SwapMatchPostsGrid from '@/components/partnership-ads-modal/swap-match-posts-grid/SwapMatchPostsGrid';
import useCreatePartnershipAdMutation from '@/hooks/mutations/useCreatePartnershipAdMutation';
import useBrandsQuery from '@/hooks/queries/use-brands-query';
import useBrandedContentAdsPartnershipsQuery from '@/hooks/queries/useBrandedContentAdsPartnershipsQuery';
import useSwapMatchesQuery from '@/hooks/queries/useSwapMatchesQuery';
import useBrandId from '@/hooks/use-brand-id';
import useNavigation from '@/hooks/use-navigation';
import styles from './CreatePartnershipAd.module.scss';

function formatCurrency(n: number, currency = 'USD') {
  return new Intl.NumberFormat(undefined, {style: 'currency', currency}).format(
    n
  );
}

function CreatePartnershipAd() {
  const {partnershipId} = useParams<{partnershipId: string}>();
  const {navigate} = useNavigation();
  const routerLocation = useLocation();
  const brandIdFromHook = useBrandId();
  const formRef = useRef<HTMLFormElement>(null);

  const [activeTab, setActiveTab] = useState<string>('select-post');
  const [selectedPost, setSelectedPost] = useState<SwapMatch | null>(null);
  const [postQuery, setPostQuery] = useState('');

  // Get partnership and brandId from location state first (passed from navigation)
  const partnershipFromState = routerLocation.state?.partnership as
    | BrandedContentAdsPartnership
    | undefined;
  const brandIdFromState = routerLocation.state?.brandId as string | undefined;

  // Use brandId from state if available, otherwise fall back to hook (which reads from URL)
  const brandId = brandIdFromState || brandIdFromHook;

  // Fallback: try to get from query if not in state
  const {
    data: partnerships = [],
    isLoading: isLoadingPartnerships,
    isError: isPartnershipsError,
  } = useBrandedContentAdsPartnershipsQuery({
    brandId: brandId || '',
    enabled: !!brandId && !partnershipFromState, // Only query if we don't have it from state
  });

  // Use partnership from state, or find it from query results
  const partnership = useMemo(() => {
    if (partnershipFromState) {
      return partnershipFromState;
    }
    if (!partnershipId) return null;
    return partnerships.find((p) => p.id === partnershipId) || null;
  }, [partnershipFromState, partnershipId, partnerships]);

  // Get profile ID - check both profile and identity.profile
  const profileId = partnership
    ? partnership.profile?.id || partnership.identity?.profile?.id
    : undefined;

  const {data: swapMatches = [], isLoading: isLoadingPosts} =
    useSwapMatchesQuery({
      brandId,
      profileId,
      enabled: !!brandId && !!profileId,
    });

  // Get brand data for display name
  const {data: brands = []} = useBrandsQuery({
    brandId: brandId || '',
    enabled: !!brandId,
  });

  const currentBrand = useMemo(() => {
    if (!brandId) return null;
    return brands.find((b: {id: string}) => String(b.id) === String(brandId));
  }, [brands, brandId]);

  const filteredPosts = useMemo(
    () =>
      swapMatches.filter((p) =>
        (p.body || '').toLowerCase().includes(postQuery.toLowerCase())
      ),
    [swapMatches, postQuery]
  );

  // Form state
  const [budgetType, setBudgetType] = useState<'daily' | 'lifetime'>('daily');
  const [budgetAmount, setBudgetAmount] = useState<string>('25');
  const today = new Date();
  const oneWeekFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date | null>(oneWeekFromNow);
  const [ageMin, setAgeMin] = useState<number>(18);
  const [ageMax, setAgeMax] = useState<number>(65);
  const [genders, setGenders] = useState<string[]>(['all']);
  const [targetLocation, setTargetLocation] = useState('US');

  const days = useMemo(() => {
    if (!endDate) return 1;
    const s = new Date(startDate);
    const e = new Date(endDate);
    const diff = Math.max(1, Math.ceil((e.getTime() - s.getTime()) / 86400000));
    return diff;
  }, [startDate, endDate]);

  const createPartnershipAdMutation = useCreatePartnershipAdMutation({
    onSuccess: () => {
      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        icon: (
          <Icon
            size="large"
            appearance="brand"
            name="Actions-Patch-Accept-Line"
          />
        ),
        message: (
          <FlashMessage.Banner
            title={translate('Partnership ad created successfully!')}
            subtitle={translate('The ad will begin running once approved.')}
          />
        ),
      });
      navigate('/partnerships');
    },
    onError: (mutationError) => {
      raiseFlashMessageError({
        title: translate('Failed to create partnership ad'),
        subtitle: translate(
          'Please try again or contact support if the issue persists.'
        ),
      });
      Sentry.captureException(mutationError, {
        message: 'Error creating partnership ad',
        level: 'error',
        type: 'error',
      });
    },
  });

  const handlePostSelect = (post: SwapMatch) => {
    setSelectedPost(post);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!partnership || !selectedPost || !startDate || !brandId) {
      return;
    }

    const formData: PartnershipAdFormData = {
      budgetType,
      budgetAmount: parseFloat(budgetAmount),
      startDate,
      endDate: endDate || null,
      ageMin,
      ageMax,
      genders,
      locations: [targetLocation],
    };

    createPartnershipAdMutation.mutate({
      brandId,
      swapMatchId: selectedPost.id,
      formData,
    });
  };

  const canPurchase =
    Boolean(brandId) &&
    Boolean(selectedPost) &&
    parseFloat(budgetAmount) >= 1 &&
    days >= 1;

  if (!partnershipId) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="error">{translate('Invalid partnership ID')}</Alert>
      </Box>
    );
  }

  // Show loading only if we don't have partnership from state and query is loading
  if (!partnershipFromState && isLoadingPartnerships) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error only if we don't have partnership from state and query failed
  if (isPartnershipsError && !partnershipFromState) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="error">
          {translate('Failed to load partnership. Please try again.')}
        </Alert>
      </Box>
    );
  }

  // Show error if we still don't have partnership after all attempts
  // But only if we're not still loading
  if (!partnership && !isLoadingPartnerships && !partnershipFromState) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="error">{translate('Partnership not found')}</Alert>
        <Box sx={{mt: 2}}>
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {translate('Please go back and try again.')}
          </Body>
        </Box>
      </Box>
    );
  }

  // If we don't have partnership yet, show loading
  if (!partnership) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const creatorName =
    partnership.profile?.displayName ||
    partnership.identity?.cachedHandle ||
    translate('Unknown Creator');

  const creatorProfileImage =
    partnership.profile?.imageUrl ||
    partnership.identity?.cachedProfileImageUrl ||
    '';

  const creatorHandle = partnership.identity?.cachedHandle || '';

  const brandName = currentBrand?.displayName || currentBrand?.name || '';

  const tabsData: TabData[] = [
    {label: translate('1. Select Post'), value: 'select-post'},
    {label: translate('2. Budget & Schedule'), value: 'budget-schedule'},
    {label: translate('3. Audience'), value: 'audience'},
  ];

  return (
    <Box className={styles.container}>
      <Box className={styles.content}>
        <Box className={styles.mainContent}>
          <Box className={styles.topBar}>
            <Button
              onClick={() => navigate('/partnerships')}
              appearance="neutral"
              mode="plain"
              size="medium"
              className={styles.backButton}
              icon={
                <Icon
                  size="medium"
                  appearance="neutral"
                  name="Actions-Arrow-Back-Small"
                  color={designSystemToken('semantic.fg.secondary')}
                />
              }
            >
              {translate('Back to Partnership Creators List')}
            </Button>
          </Box>
          <Box className={styles.header}>
            <Box>
              <Headline size="lg" className={styles.title}>
                {translate('Create Partnership Ad')}
              </Headline>
              <Body
                size="md"
                color={designSystemToken('semantic.fg.secondary')}
              >
                {translate('Create a partnership ad with')} {creatorName}
              </Body>
            </Box>
          </Box>

          <Card className={styles.card}>
            <CardContent>
              <Tabs
                value={activeTab}
                onChange={(_, tabId) => setActiveTab(tabId)}
                tabsData={tabsData}
                className={styles.tabs}
                size="large"
                appearance="neutral"
              />
              <Box sx={{pt: 2}}>
                {activeTab === 'select-post' && (
                  <>
                    <Box className={styles.searchContainer}>
                      <TextField
                        fullWidth
                        value={postQuery}
                        onChange={(e) => setPostQuery(e.target.value)}
                        placeholder={translate('Search your posts…')}
                        size="small"
                      />
                      <Chip
                        label={`${filteredPosts.length} ${translate('results')}`}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderColor: designSystemToken(
                            'semantic.bg.neutral-inverse'
                          ),
                        }}
                      />
                    </Box>
                    <Box className={styles.postsGridContainer}>
                      <SwapMatchPostsGrid
                        posts={filteredPosts}
                        selectedPostId={selectedPost?.id || null}
                        onPostSelect={handlePostSelect}
                        isLoading={isLoadingPosts}
                      />
                    </Box>
                  </>
                )}

                {activeTab === 'budget-schedule' && (
                  <Box className={styles.budgetGrid}>
                    <Box className={styles.budgetFields}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={budgetType === 'daily'}
                            onChange={(e) =>
                              setBudgetType(
                                e.target.checked ? 'daily' : 'lifetime'
                              )
                            }
                          />
                        }
                        label={
                          budgetType === 'daily'
                            ? translate('Daily budget')
                            : translate('Lifetime budget')
                        }
                      />

                      <Box className={styles.budgetInputContainer}>
                        <TextField
                          label={
                            budgetType === 'daily'
                              ? translate('Daily budget')
                              : translate('Total budget')
                          }
                          type="number"
                          value={budgetAmount}
                          onChange={(e) => setBudgetAmount(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          }}
                          required
                          fullWidth
                        />
                      </Box>

                      <Box className={styles.dateFields}>
                        <TextField
                          label={translate('Start date')}
                          type="date"
                          value={startDate.toISOString().split('T')[0]}
                          onChange={(e) =>
                            setStartDate(new Date(e.target.value))
                          }
                          InputLabelProps={{shrink: true}}
                          inputProps={{
                            min: today.toISOString().split('T')[0],
                          }}
                          required
                          fullWidth
                        />
                        <TextField
                          label={translate('End date')}
                          type="date"
                          value={
                            endDate ? endDate.toISOString().split('T')[0] : ''
                          }
                          onChange={(e) =>
                            setEndDate(
                              e.target.value ? new Date(e.target.value) : null
                            )
                          }
                          InputLabelProps={{shrink: true}}
                          inputProps={{
                            min: startDate
                              ? startDate.toISOString().split('T')[0]
                              : today.toISOString().split('T')[0],
                          }}
                          fullWidth
                        />
                      </Box>

                      <Body
                        size="sm"
                        color={designSystemToken('semantic.fg.secondary')}
                      >
                        {translate('Campaign length:')}{' '}
                        <strong>
                          {days}{' '}
                          {days === 1 ? translate('day') : translate('days')}
                        </strong>
                      </Body>
                    </Box>
                  </Box>
                )}

                {activeTab === 'audience' && (
                  <Box className={styles.audienceGrid}>
                    <Box className={styles.audienceFields}>
                      <FormControl fullWidth>
                        <InputLabel>{translate('Location')}</InputLabel>
                        <Select
                          value={targetLocation}
                          onChange={(e) => setTargetLocation(e.target.value)}
                          label={translate('Location')}
                        >
                          <MenuItem value="US">
                            {translate('United States')}
                          </MenuItem>
                          <MenuItem value="CA">{translate('Canada')}</MenuItem>
                          <MenuItem value="GB">
                            {translate('United Kingdom')}
                          </MenuItem>
                          <MenuItem value="AU">
                            {translate('Australia')}
                          </MenuItem>
                          <MenuItem value="WW">
                            {translate('Worldwide')}
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <Box>
                        <Label size="sm">{translate('Age range')}</Label>
                        <Box sx={{px: 2, pt: 1}}>
                          <Slider
                            value={[ageMin, ageMax]}
                            onChange={(_, newValue) => {
                              const [min, max] = newValue as number[];
                              setAgeMin(min);
                              setAgeMax(max);
                            }}
                            min={13}
                            max={65}
                            valueLabelDisplay="auto"
                          />
                        </Box>
                        <Body
                          size="sm"
                          color={designSystemToken('semantic.fg.secondary')}
                        >
                          {ageMin} – {ageMax} {translate('years')}
                        </Body>
                      </Box>

                      <Box>
                        <Label size="sm">{translate('Genders')}</Label>
                        <Box className={styles.genderCheckboxes}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={genders.includes('all')}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setGenders(['all']);
                                  } else {
                                    setGenders([]);
                                  }
                                }}
                              />
                            }
                            label={translate('All Genders')}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={genders.includes('male')}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setGenders([
                                      ...genders.filter((g) => g !== 'all'),
                                      'male',
                                    ]);
                                  } else {
                                    setGenders(
                                      genders.filter((g) => g !== 'male')
                                    );
                                  }
                                }}
                              />
                            }
                            label={translate('Male')}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={genders.includes('female')}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setGenders([
                                      ...genders.filter((g) => g !== 'all'),
                                      'female',
                                    ]);
                                  } else {
                                    setGenders(
                                      genders.filter((g) => g !== 'female')
                                    );
                                  }
                                }}
                              />
                            }
                            label={translate('Female')}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Card className={styles.sidebar}>
          <CardContent>
            <Box className={styles.sidebarHeader}>
              <Icon
                name="Actions-Patch-Accept-Line"
                size="small"
                appearance="primary"
              />
              <Headline size="sm">{translate('Review & purchase')}</Headline>
            </Box>
            <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
              {translate('Confirm details before sending your ad for review.')}
            </Body>

            {selectedPost && (
              <>
                <Divider sx={{my: 2}} />
                <InstagramPostPreview
                  post={selectedPost}
                  creatorName={creatorName}
                  creatorProfileImage={creatorProfileImage}
                  creatorHandle={creatorHandle}
                  brandName={brandName}
                />
              </>
            )}

            <Divider sx={{my: 2}} />

            <Box className={styles.summaryGrid}>
              <Box className={styles.summaryItem}>
                <Body
                  size="sm"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {translate('Budget')}
                </Body>
                <Body size="md">
                  {formatCurrency(parseFloat(budgetAmount) || 0, 'USD')}{' '}
                  {budgetType === 'daily'
                    ? translate('/ day')
                    : translate('total')}
                </Body>
              </Box>
              <Box className={styles.summaryItem}>
                <Body
                  size="sm"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {translate('Audience')}
                </Body>
                <Body size="md">
                  {targetLocation}, {ageMin}–{ageMax} {translate('yrs')}
                </Body>
              </Box>
              <Box className={styles.summaryItem}>
                <Body
                  size="sm"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {translate('Start date')}
                </Body>
                <Body size="md">{startDate.toISOString().split('T')[0]}</Body>
              </Box>
              <Box className={styles.summaryItem}>
                <Body
                  size="sm"
                  color={designSystemToken('semantic.fg.secondary')}
                >
                  {translate('End date')}
                </Body>
                <Body size="md">
                  {endDate
                    ? endDate.toISOString().split('T')[0]
                    : translate('Ongoing')}
                </Body>
              </Box>
            </Box>

            <Divider sx={{my: 2}} />

            <form ref={formRef} onSubmit={handleFormSubmit}>
              <Button
                type="submit"
                appearance="neutral"
                mode="filled"
                size="medium"
                disabled={!canPurchase || createPartnershipAdMutation.isLoading}
                fullWidth
              >
                {createPartnershipAdMutation.isLoading
                  ? translate('Creating Ad...')
                  : translate('Purchase & Submit for Review')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

export default CreatePartnershipAd;
